import type { GraphEdge, GraphNode, StrategyDocument, StrategyPatchOp } from "@/lib/types";
import { Card, CardTitle } from "@/components/Card";
import {
  BoolField,
  ChipListField,
  FieldRow,
  NumberField,
  SMALL_BUTTON_CLS,
  TextField,
  TextListField,
} from "@/components/strategy/FieldEditors";
import { usePhaseEditor, type PhaseEditorProps } from "@/components/strategy/phaseEditing";
import PhaseEmailCard from "@/components/strategy/PhaseEmailCard";
import TransitionsEditor, { TransitionList } from "@/components/strategy/TransitionsEditor";
import PhaseQuestionsCard from "@/components/strategy/PhaseQuestionsCard";
import ComputedFieldsCard from "@/components/strategy/ComputedFieldsCard";
import PhaseOutputCard from "@/components/strategy/PhaseOutputCard";
import PhaseYamlEditor from "@/components/strategy/PhaseYamlEditor";

/**
 * Everything configured on one phase, opened from its flowchart node. For an
 * admin with the strategy document loaded, every key of the phase block has a
 * control here — edits accumulate into the page's patch set and are saved
 * together from the page header. The raw YAML block at the bottom is the same
 * data as text, for pasting or for shapes the form does not draw.
 */
export default function PhaseInspector({
  strategySlug,
  strategyId,
  node,
  outgoing,
  incoming,
  canEditYaml,
  onSaved,
  document,
  pendingOps,
  onEdit,
}: {
  strategySlug: string;
  /** Persisted strategy id, for lookups keyed by id (document templates). */
  strategyId: string | null;
  node: GraphNode;
  outgoing: GraphEdge[];
  incoming: GraphEdge[];
  canEditYaml: boolean;
  onSaved: () => void;
  /** Draft document (saved + pending ops); null when the viewer is not admin. */
  document: StrategyDocument | null;
  pendingOps: StrategyPatchOp[];
  onEdit: (op: StrategyPatchOp) => void;
}) {
  const editorProps: PhaseEditorProps | null = document
    ? { node, document, pendingOps, onEdit }
    : null;

  return (
    <div className="space-y-4" data-testid="phase-inspector">
      <Card>
        {editorProps ? (
          <PhaseHeader {...editorProps} />
        ) : (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <CardTitle>{node.name}</CardTitle>
              <code className="text-xs text-[var(--fg-muted)]">{node.slug}</code>
            </div>
            <span className="shrink-0 text-xs text-[var(--fg-muted)]">step {node.order}</span>
          </div>
        )}

        {editorProps ? (
          <PhaseForm {...editorProps} />
        ) : (
          <ReadOnlySummary node={node} />
        )}
      </Card>

      {editorProps ? (
        <>
          <PhaseEmailCard {...editorProps} />
          <TransitionsEditor {...editorProps} outgoing={outgoing} incoming={incoming} />
          <PhaseQuestionsCard {...editorProps} />
          <ComputedFieldsCard {...editorProps} />
          <PhaseOutputCard {...editorProps} strategyId={strategyId} />
        </>
      ) : (
        <Card>
          <CardTitle>Transitions</CardTitle>
          <TransitionList title="Leaves this phase when" edges={outgoing} direction="out" />
          <TransitionList title="Arrives here from" edges={incoming} direction="in" />
        </Card>
      )}

      {canEditYaml ? (
        <PhaseYamlEditor
          strategySlug={strategySlug}
          phaseSlug={node.slug}
          onSaved={onSaved}
        />
      ) : null}
    </div>
  );
}

/**
 * Name and position. The slug is the phase's identity (rules point at it) and
 * stays read-only; the position is changed by swapping `order` with the
 * neighbouring phase, so the strategy's unique-order rule always holds.
 */
function PhaseHeader(props: PhaseEditorProps) {
  const { node, document, onEdit } = props;
  const { str, num, edit, isEdited } = usePhaseEditor(props);
  const ordered = [...(document.strategy?.phases ?? [])]
    .filter((p) => typeof p.order === "number")
    .sort((a, b) => (a.order as number) - (b.order as number));
  const index = ordered.findIndex((p) => (p.id ?? p.slug) === node.slug);
  const order = num(["order"]);

  const swapWith = (neighbourIndex: number) => {
    const neighbour = ordered[neighbourIndex];
    if (!neighbour || order === null) return;
    const neighbourSlug = String(neighbour.id ?? neighbour.slug);
    edit(["order"], neighbour.order);
    onEdit({ scope: "phase", phase: neighbourSlug, keyPath: ["order"], value: order });
  };

  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <FieldRow label="Name" edited={isEdited(["name"])}>
          <TextField
            ariaLabel="Phase name"
            value={str(["name"])}
            onCommit={(v) => {
              if (v !== null) edit(["name"], v);
            }}
          />
        </FieldRow>
        <code className="text-xs text-[var(--fg-muted)]">{node.slug}</code>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1 text-xs text-[var(--fg-muted)]">
        <span className={isEdited(["order"]) ? "text-amber-700" : undefined}>
          step {order ?? node.order}
        </span>
        <div className="flex gap-1">
          <button
            type="button"
            aria-label="Move phase earlier"
            className={SMALL_BUTTON_CLS}
            disabled={index <= 0}
            onClick={() => swapWith(index - 1)}
          >
            ↑
          </button>
          <button
            type="button"
            aria-label="Move phase later"
            className={SMALL_BUTTON_CLS}
            disabled={index < 0 || index >= ordered.length - 1}
            onClick={() => swapWith(index + 1)}
          >
            ↓
          </button>
        </div>
      </div>
    </div>
  );
}

/** The scalar and list keys of the phase block — each field is one patch op. */
function PhaseForm(props: PhaseEditorProps) {
  const { read, str, num, list, edit, isEdited } = usePhaseEditor(props);

  return (
    <div className="mt-3 space-y-3">
      <FieldRow
        label="Goal"
        edited={isEdited(["goal"])}
        hint="What the agent is working towards while a prospect sits here. It goes into the email and reply-analysis prompts and into the query used to retrieve knowledge — so write an objective, not a description."
      >
        <TextField
          ariaLabel="Phase goal"
          multiline
          value={str(["goal"])}
          onCommit={(v) => edit(["goal"], v)}
          placeholder="What this phase is trying to achieve"
        />
      </FieldRow>

      <div className="grid grid-cols-3 gap-2">
        <FieldRow
          label="Delay (hours)"
          edited={isEdited(["delay_between_emails_hours"])}
          hint="Wait after a send before the agent acts again here. Empty = it does not re-schedule itself; the conversation then moves only on a reply or a manual step."
        >
          <NumberField
            ariaLabel="Delay between emails in hours"
            min={0}
            value={num(["delay_between_emails_hours"])}
            onCommit={(v) => edit(["delay_between_emails_hours"], v)}
          />
        </FieldRow>
        <FieldRow
          label="Max emails"
          edited={isEdited(["max_emails_in_sequence"])}
          hint="Most messages the agent sends from this phase before it stops. Empty = no limit."
        >
          <NumberField
            ariaLabel="Maximum emails in sequence"
            min={1}
            value={num(["max_emails_in_sequence"])}
            onCommit={(v) => edit(["max_emails_in_sequence"], v)}
          />
        </FieldRow>
        <FieldRow
          label="Cool-off (days)"
          edited={isEdited(["cool_off_days"])}
          hint="A silence this long refills the budget and a new run begins. Empty = the cap is final while the prospect stays here."
        >
          <NumberField
            ariaLabel="Cool off days"
            min={0}
            value={num(["cool_off_days"])}
            onCommit={(v) => edit(["cool_off_days"], v)}
          />
        </FieldRow>
      </div>

      <FieldRow
        label="Terminal"
        edited={isEdited(["terminal"])}
        hint="A terminal phase ends the conversation — no further outbound."
      >
        <BoolField
          label="prospects stop here"
          value={read(["terminal"]) === true}
          onCommit={(v) => edit(["terminal"], v)}
        />
      </FieldRow>

      <FieldRow
        label="Knowledge topics"
        edited={isEdited(["rag_filter", "topics"])}
        hint="Filters which knowledge-base passages this phase retrieves."
      >
        <ChipListField
          addLabel="Add topic"
          value={list(["rag_filter", "topics"])}
          onCommit={(v) => edit(["rag_filter", "topics"], v)}
        />
      </FieldRow>

      <FieldRow
        label="Manual prompt"
        edited={isEdited(["manual_prompt"])}
        hint="Standing guidance rendered into every email this phase writes."
      >
        <TextField
          ariaLabel="Manual prompt"
          multiline
          value={str(["manual_prompt"])}
          onCommit={(v) => edit(["manual_prompt"], v)}
        />
      </FieldRow>

      <FieldRow
        label="Safeguards (this phase)"
        edited={isEdited(["safeguards"])}
        hint="Non-negotiable rules added to every prompt while a prospect is in this phase, on top of the strategy-wide safeguards."
      >
        <TextListField
          addLabel="Add safeguard"
          value={list(["safeguards"])}
          onCommit={(v) => edit(["safeguards"], v)}
        />
      </FieldRow>

      <FieldRow
        label="AI rules"
        edited={isEdited(["ai_rules"])}
        hint="Business rules rendered into the email-writing prompt for this phase."
      >
        <TextListField
          addLabel="Add rule"
          value={list(["ai_rules"])}
          onCommit={(v) => edit(["ai_rules"], v)}
        />
      </FieldRow>
    </div>
  );
}

function ReadOnlySummary({ node }: { node: GraphNode }) {
  return (
    <>
      {node.goal ? (
        <p className="mt-2 text-sm text-zinc-700">{node.goal}</p>
      ) : (
        <p className="mt-2 text-sm text-[var(--fg-muted)]">No goal configured.</p>
      )}
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
        <Fact label="Terminal" value={node.terminal ? "yes" : "no"} />
        <Fact label="Active prompts" value={String(node.activeManualPrompts)} />
        <Fact label="Qualification questions" value={String(node.qualificationQuestionCount)} />
        <Fact label="AI rules" value={String(node.aiRuleCount)} />
        <Fact label="Computed fields" value={String(node.computedFieldCount)} />
        <Fact label="Document output" value={node.hasDocumentOutput ? "yes" : "no"} />
      </dl>
      {node.ragTopics.length > 0 ? (
        <div className="mt-3">
          <div className="text-xs text-[var(--fg-muted)] mb-1">Knowledge topics</div>
          <div className="flex flex-wrap gap-1">
            {node.ragTopics.map((topic) => (
              <span
                key={topic}
                className="rounded border border-[var(--border)] px-1.5 py-0.5 text-[11px]"
              >
                {topic}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[var(--fg-muted)]">{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  );
}
